// The data was originally taken from the form react-phone-input-2
// https://github.com/bl00mber/react-phone-input-2/blob/master/src/rawCountries.js

export type CountryIso2 = string;

// Base country data tuple
type BaseCountryData = [
  string, // Country name
  CountryIso2, // ISO2 code
  string // International dial code
];

// Country data with format (regex patterns for formatting)
type CountryDataWithFormat = [
  ...BaseCountryData,
  Record<string, string> | string // Format config or simple format string
];

// Country data with priority order (for countries sharing same dial code)
export type CountryDataWithOrder =
  | [string, string, string, string | Record<string, string>, number]
  | [string, string, string, string | Record<string, string>, number, string[]];

// Full country data type supporting all variations
export type CountryData =
  | BaseCountryData
  | CountryDataWithFormat
  | CountryDataWithOrder;

// Parsed country after reading from data
export interface ParsedCountry {
  name: CountryData[0];
  iso2: CountryData[1];
  dialCode: CountryData[2];
  format?: CountryData[3];
  priority?: CountryData[4];
}

// Country list with formatting and ordering
export const defaultCountries: CountryDataWithOrder[] = [
  [
    "United Arab Emirates",
    "ae",
    "971",
    {
      default: ".. ... ....",
      "/^50/": "... ... ...",
      "/^(2|3|4|6|7|9)/": ". ... ...",
    },
    0,
  ],
  [
    "Qatar",
    "qa",
    "974",
    {
      default: ".... ....",
      "/^4/": ".... ....",
      "/^6[0-3]/": ".. ... ..",
    },
    1,
  ],
  [
    "Bahrain",
    "bh",
    "973",
    {
      default: ".... ....",
      "/^4/": ".... ....",
      "/^6[0-3]/": ".. ... ..",
    },
    2,
  ],
  ["Afghanistan", "af", "93", "", 0],
  ["Albania", "al", "355", "", 0],
  ["Algeria", "dz", "213", "", 0],
  ["Andorra", "ad", "376", "", 0],
  ["Angola", "ao", "244", "", 0],
  ["Antigua and Barbuda", "ag", "1268", "", 0],
  [
    "Argentina",
    "ar",
    "54",
    {
      default: "(..) .... ....",
      "/^11/": "(..) .... ....",
      "/^15/": "(..) ... ....",
      "/^(2|3|4|5)/": "(.) .... ....",
      "/^9/": "(.) .... .....",
    },
    0,
  ],
  ["Armenia", "am", "374", ".. ......", 0],
  ["Aruba", "aw", "297", "", 0],
  [
    "Australia",
    "au",
    "61",
    {
      default: ". .... ....",
      "/^4/": "... ... ...",
      "/^5(?!50)/": "... ... ...",
      "/^1(3|8)00/": ".... ... ...",
      "/^13/": ".. .. ..",
      "/^180/": "... ....",
    },
    0,
    [],
  ],
  ["Austria", "at", "43", "", 0],
  ["Azerbaijan", "az", "994", "(..) ... .. ..", 0],
  ["Bahamas", "bs", "1242", "", 0],
  ["Bangladesh", "bd", "880", "", 0],
  ["Barbados", "bb", "1246", "", 0],
  ["Belarus", "by", "375", "(..) ... .. ..", 0],
  ["Belgium", "be", "32", "... .. .. ..", 0],
  ["Belize", "bz", "501", "", 0],
  ["Benin", "bj", "229", "", 0],
  ["Bhutan", "bt", "975", "", 0],
  ["Bolivia", "bo", "591", "", 0],
  ["Bosnia and Herzegovina", "ba", "387", "", 0],
  ["Botswana", "bw", "267", "", 0],
  ["Brazil", "br", "55", "(..) .....-....", 0],
  ["British Indian Ocean Territory", "io", "246", "", 0],
  ["Brunei", "bn", "673", "", 0],
  ["Bulgaria", "bg", "359", "", 0],
  ["Burkina Faso", "bf", "226", "", 0],
  ["Burundi", "bi", "257", "", 0],
  ["Cambodia", "kh", "855", "", 0],
  ["Cameroon", "cm", "237", "", 0],
  [
    "Canada",
    "ca",
    "1",
    "(...) ...-....",
    1,
    [
      "204",
      "226",
      "236",
      "249",
      "250",
      "289",
      "306",
      "343",
      "365",
      "387",
      "403",
      "416",
      "418",
      "431",
      "437",
      "438",
      "450",
      "506",
      "514",
      "519",
      "548",
      "579",
      "581",
      "587",
      "604",
      "613",
      "639",
      "647",
      "672",
      "705",
      "709",
      "742",
      "778",
      "780",
      "782",
      "807",
      "819",
      "825",
      "867",
      "873",
      "902",
      "905",
    ],
  ],
  ["Cape Verde", "cv", "238", "", 0],
  ["Caribbean Netherlands", "bq", "599", "", 1],
  ["Cayman Islands", "ky", "1", "... ... ....", 4, ["345"]],
  ["Central African Republic", "cf", "236", "", 0],
  ["Chad", "td", "235", "", 0],
  ["Chile", "cl", "56", "", 0],
  ["China", "cn", "86", "... .... ....", 0],
  ["Colombia", "co", "57", "... ... ....", 0],
  ["Comoros", "km", "269", "", 0],
  ["Congo", "cd", "243", "", 0],
  ["Congo", "cg", "242", "", 0],
  ["Costa Rica", "cr", "506", "....-....", 0],
  ["Côte d'Ivoire", "ci", "225", ".. .. .. .. ..", 0],
  ["Croatia", "hr", "385", "", 0],
  ["Cuba", "cu", "53", "", 0],
  ["Curaçao", "cw", "599", "", 0],
  ["Cyprus", "cy", "357", ".. ......", 0],
  ["Czech Republic", "cz", "420", "... ... ...", 0],
  ["Denmark", "dk", "45", ".. .. .. ..", 0],
  ["Djibouti", "dj", "253", ".. .. ....", 0],
  ["Dominica", "dm", "1767", "", 0],
  ["Dominican Republic", "do", "1", "(...) ...-....", 2, ["809", "829", "849"]],
  ["Ecuador", "ec", "593", "", 0],
  ["Egypt", "eg", "20", "", 0],
  ["El Salvador", "sv", "503", "....-....", 0],
  ["Equatorial Guinea", "gq", "240", "", 0],
  ["Eritrea", "er", "291", "", 0],
  ["Estonia", "ee", "372", ".... ......", 0],
  ["Ethiopia", "et", "251", ".. ... ....", 0],
  ["Faroe Islands", "fo", "298", ".. .. ..", 0],
  ["Fiji", "fj", "679", "", 0],
  ["Finland", "fi", "358", ".. ... .. ..", 0],
  ["France", "fr", "33", ". .. .. .. ..", 0],
  ["French Guiana", "gf", "594", "... .. .. ..", 0],
  [
    "French Polynesia",
    "pf",
    "689",
    {
      "/^44/": ".. .. ..",
      "/^80[0-5]/": "... .. .. ..",
      default: ".. .. .. ..",
    },
    0,
  ],
  ["Gabon", "ga", "241", "", 0],
  ["Gambia", "gm", "220", "", 0],
  ["Georgia", "ge", "995", "", 0],
  ["Germany", "de", "49", "... .........", 0],
  ["Ghana", "gh", "233", "", 0],
  ["Greece", "gr", "30", "", 0],
  ["Greenland", "gl", "299", ".. .. ..", 0],
  ["Grenada", "gd", "1473", "", 0],
  ["Guadeloupe", "gp", "590", "... .. .. ..", 0],
  ["Guam", "gu", "1671", "", 0],
  ["Guatemala", "gt", "502", "....-....", 0],
  ["Guinea", "gn", "224", "", 0],
  ["Guinea-Bissau", "gw", "245", "", 0],
  ["Guyana", "gy", "592", "", 0],
  ["Haiti", "ht", "509", "....-....", 0],
  ["Honduras", "hn", "504", "", 0],
  ["Hong Kong", "hk", "852", ".... ....", 0],
  ["Hungary", "hu", "36", "", 0],
  ["Iceland", "is", "354", "... ....", 0],
  ["India", "in", "91", ".....-.....", 0],
  ["Indonesia", "id", "62", "", 0],
  ["Iran", "ir", "98", "... ... ....", 0],
  ["Iraq", "iq", "964", "", 0],
  ["Ireland", "ie", "353", ".. .......", 0],
  ["Israel", "il", "972", "... ... ....", 0],
  ["Italy", "it", "39", "... .......", 0],
  ["Jamaica", "jm", "1876", "", 0],
  ["Japan", "jp", "81", ".. .... ....", 0],
  ["Jordan", "jo", "962", "", 0],
  ["Kazakhstan", "kz", "7", "... ...-..-..", 0],
  ["Kenya", "ke", "254", "", 0],
  ["Kiribati", "ki", "686", "", 0],
  ["Kosovo", "xk", "383", "", 0],
  ["Kuwait", "kw", "965", "", 0],
  ["Kyrgyzstan", "kg", "996", "... ... ...", 0],
  ["Laos", "la", "856", "", 0],
  ["Latvia", "lv", "371", ".. ... ...", 0],
  ["Lebanon", "lb", "961", "", 0],
  ["Lesotho", "ls", "266", "", 0],
  ["Liberia", "lr", "231", "", 0],
  ["Libya", "ly", "218", "", 0],
  ["Liechtenstein", "li", "423", "", 0],
  ["Lithuania", "lt", "370", "", 0],
  ["Luxembourg", "lu", "352", "", 0],
  ["Macau", "mo", "853", "", 0],
  ["Macedonia", "mk", "389", "", 0],
  ["Madagascar", "mg", "261", "", 0],
  ["Malawi", "mw", "265", "", 0],
  ["Malaysia", "my", "60", "..-....-....", 0],
  ["Maldives", "mv", "960", "", 0],
  ["Mali", "ml", "223", "", 0],
  ["Malta", "mt", "356", "", 0],
  ["Marshall Islands", "mh", "692", "", 0],
  ["Martinique", "mq", "596", "... .. .. ..", 0],
  ["Mauritania", "mr", "222", "", 0],
  ["Mauritius", "mu", "230", "", 0],
  ["Mayotte", "yt", "262", "... .. .. ..", 1, ["269", "639"]],
  ["Mexico", "mx", "52", "... ... ....", 0],
  ["Micronesia", "fm", "691", "", 0],
  ["Moldova", "md", "373", "(..) ..-..-..", 0],
  ["Monaco", "mc", "377", "", 0],
  ["Mongolia", "mn", "976", "", 0],
  ["Montenegro", "me", "382", "", 0],
  ["Morocco", "ma", "212", "", 0],
  ["Mozambique", "mz", "258", "", 0],
  ["Myanmar", "mm", "95", "", 0],
  ["Namibia", "na", "264", "", 0],
  ["Nauru", "nr", "674", "", 0],
  ["Nepal", "np", "977", "", 0],
  [
    "Netherlands",
    "nl",
    "31",
    {
      "/^06/": "(.). .........",
      "/^6/": ". .........",
      "/^0(10|13|14|15|20|23|24|26|30|33|35|36|38|40|43|44|45|46|50|53|55|58|70|71|72|73|74|75|76|77|78|79|82|84|85|87|88|91)/":
        "(.).. ........",
      "/^(10|13|14|15|20|23|24|26|30|33|35|36|38|40|43|44|45|46|50|53|55|58|70|71|72|73|74|75|76|77|78|79|82|84|85|87|88|91)/":
        ".. ........",
      "/^0/": "(.)... .......",
      default: "... .......",
    },
    0,
  ],
  ["New Caledonia", "nc", "687", "", 0],
  ["New Zealand", "nz", "64", "...-...-....", 0],
  ["Nicaragua", "ni", "505", "", 0],
  ["Niger", "ne", "227", "", 0],
  ["Nigeria", "ng", "234", "", 0],
  ["North Korea", "kp", "850", "", 0],
  ["Norway", "no", "47", "... .. ...", 0],
  ["Oman", "om", "968", "", 0],
  ["Pakistan", "pk", "92", "...-.......", 0],
  ["Palau", "pw", "680", "", 0],
  ["Palestine", "ps", "970", "", 0],
  ["Panama", "pa", "507", "", 0],
  ["Papua New Guinea", "pg", "675", "", 0],
  ["Paraguay", "py", "595", "", 0],
  ["Peru", "pe", "51", "", 0],
  ["Philippines", "ph", "63", "... ... ....", 0],
  ["Poland", "pl", "48", "...-...-...", 0],
  ["Portugal", "pt", "351", "", 0],
  ["Puerto Rico", "pr", "1", "(...) ...-....", 3, ["787", "939"]],
  ["Réunion", "re", "262", "... .. .. ..", 0],
  ["Romania", "ro", "40", "", 0],
  ["Russia", "ru", "7", "(...) ...-..-..", 1],
  ["Rwanda", "rw", "250", "", 0],
  ["Saint Kitts and Nevis", "kn", "1869", "", 0],
  ["Saint Lucia", "lc", "1758", "", 0],
  [
    "Saint Pierre & Miquelon",
    "pm",
    "508",
    {
      "/^708/": "... ... ...",
      "/^8/": "... .. .. ..",
      default: ".. .. ..",
    },
    0,
  ],
  ["Saint Vincent and the Grenadines", "vc", "1784", "", 0],
  ["Samoa", "ws", "685", "", 0],
  ["San Marino", "sm", "378", "", 0],
  ["São Tomé and Príncipe", "st", "239", "", 0],
  ["Saudi Arabia", "sa", "966", "", 0],
  ["Senegal", "sn", "221", "", 0],
  ["Serbia", "rs", "381", "", 0],
  ["Seychelles", "sc", "248", "", 0],
  ["Sierra Leone", "sl", "232", "", 0],
  ["Singapore", "sg", "65", "....-....", 0],
  ["Slovakia", "sk", "421", "", 0],
  ["Slovenia", "si", "386", "", 0],
  ["Solomon Islands", "sb", "677", "", 0],
  ["Somalia", "so", "252", "", 0],
  ["South Africa", "za", "27", "", 0],
  ["South Korea", "kr", "82", "... .... ....", 0],
  ["South Sudan", "ss", "211", "", 0],
  ["Spain", "es", "34", "... ... ...", 0],
  ["Sri Lanka", "lk", "94", "", 0],
  ["Sudan", "sd", "249", "", 0],
  ["Suriname", "sr", "597", "", 0],
  ["Swaziland", "sz", "268", "", 0],
  ["Sweden", "se", "46", "... ... ...", 0],
  ["Switzerland", "ch", "41", ".. ... .. ..", 0],
  ["Syria", "sy", "963", "", 0],
  ["Taiwan", "tw", "886", "", 0],
  ["Tajikistan", "tj", "992", "", 0],
  ["Tanzania", "tz", "255", "", 0],
  ["Thailand", "th", "66", "", 0],
  ["Timor-Leste", "tl", "670", "", 0],
  ["Togo", "tg", "228", "", 0],
  ["Tonga", "to", "676", "", 0],
  ["Trinidad and Tobago", "tt", "1868", "", 0],
  ["Tunisia", "tn", "216", "", 0],
  ["Turkey", "tr", "90", "... ... .. ..", 0],
  ["Turkmenistan", "tm", "993", "", 0],
  ["Tuvalu", "tv", "688", "", 0],
  ["Uganda", "ug", "256", "", 0],
  ["Ukraine", "ua", "380", "(..) ... .. ..", 0],
  ["United Kingdom", "gb", "44", ".... ......", 0],
  ["United States", "us", "1", "(...) ...-....", 0],
  ["Uruguay", "uy", "598", "", 0],
  ["Uzbekistan", "uz", "998", ".. ... .. ..", 0],
  ["Vanuatu", "vu", "678", "", 0],
  ["Vatican City", "va", "39", ".. .... ....", 1],
  ["Venezuela", "ve", "58", "", 0],
  ["Vietnam", "vn", "84", "", 0],
  ["Wallis & Futuna", "wf", "681", ".. .. ..", 0],
  ["Yemen", "ye", "967", "", 0],
  ["Zambia", "zm", "260", "", 0],
  ["Zimbabwe", "zw", "263", "", 0],
];

// Helper function to parse country data
export const parseCountryData = (data: CountryData): ParsedCountry => {
  return {
    name: data[0],
    iso2: data[1],
    dialCode: data[2],
    format: data[3],
    priority: data[4],
  };
};

// Get country by ISO2 code
export const getCountryByIso2 = (
  iso2: CountryIso2
): ParsedCountry | undefined => {
  const countryData = defaultCountries.find((c) => c[1] === iso2);
  return countryData ? parseCountryData(countryData) : undefined;
};

// Get country by dial code (respects priority)
export const getCountryByDialCode = (
  dialCode: string
): ParsedCountry | undefined => {
  const countriesWithCode = defaultCountries
    .filter((c) => c[2] === dialCode)
    .sort((a, b) => (a[4] || 0) - (b[4] || 0)); // Sort by priority

  const countryData = countriesWithCode[0];
  return countryData ? parseCountryData(countryData) : undefined;
};

// Get all countries
export const getAllCountries = (): ParsedCountry[] => {
  return defaultCountries.map(parseCountryData);
};
